import axios from 'axios'
import { setDefaultOptions } from 'date-fns'
import { id as idLocale } from 'date-fns/locale'
import React, {
  useState,
  useContext,
  useLayoutEffect,
  useCallback,
  useMemo
} from 'react'

setDefaultOptions({
  locale: idLocale
})

import { useToast } from '@/components/ui/toast'
import { URL_WITH_AUTH } from '@/constant/url'
import { getUsersProfile } from '@/services/account'
import type { InvestorDetail } from '@/types/investor'

import { getUserInstitutions } from './services/auth'
import { InstitutionDetail, InstitutionItem } from './types/institution'
import { getErrorMessage } from './utils/error'
import { setLocalStorageConfigFeature } from './utils/localStorage'
import { getSession } from './utils/session'

export const AppContext = React.createContext<{
  profile: InvestorDetail | null
  isGold: boolean
  hasMultipleInstitutions: boolean
  institutions: InstitutionItem[]
  handleInitialize: () => void
  isInitialized: boolean
  isSidebarOpen: boolean
  setIsSidebarOpen: (open: boolean) => void
  setCurrentInstitution: (institution: InstitutionDetail | null) => void
  currentInstitution: InstitutionDetail | null
  isAccountApproved?: boolean
  handleLoadInstitution: () => void
}>({
  profile: null,
  isGold: false,
  hasMultipleInstitutions: false,
  institutions: [],
  handleInitialize: () => {},
  isInitialized: false,
  isSidebarOpen: false,
  setIsSidebarOpen: () => {},
  setCurrentInstitution: () => {},
  currentInstitution: null,
  isAccountApproved: false,
  handleLoadInstitution: () => {}
})

const getAppPathname = (): string => {
  const { hash, pathname } = window.location

  if (hash) {
    const normalized = hash.startsWith('#') ? hash.slice(1) : hash
    return normalized || '/'
  }

  return pathname || '/'
}

const AppContextProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const toast = useToast()
  const [profile, setProfile] = useState<InvestorDetail | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [institutions, setInstitutions] = useState<InstitutionItem[]>([])
  const [isGold, setIsGold] = useState(false)
  const [hasMultipleInstitutions, setHasMultipleInstitutions] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [currentInstitution, setCurrentInstitution] =
    useState<InstitutionDetail | null>(null)

  const isAccountApproved = useMemo(() => profile?.approve === 1, [profile])

  const handleFetchInstitutions = useCallback(async () => {
    try {
      const response = await getUserInstitutions()
      const responseData = response?.data?.data
      if (responseData?.institutions) {
        setInstitutions(responseData?.institutions)
        setHasMultipleInstitutions(responseData?.institutions?.length > 1)
      }
    } catch (error) {
      const message = getErrorMessage(error)
      toast({
        description: message
      })
    }
  }, [toast])

  const handleFetchUsersProfle = useCallback(async () => {
    try {
      const response = await getUsersProfile()
      const responseData: InvestorDetail = response?.data?.data
      if (responseData) {
        setProfile(responseData)
        setIsGold(responseData?.is_gold)
      }
    } catch (error) {
      const message = getErrorMessage(error)
      toast({
        description: message
      })
    }
  }, [toast])

  const handleFetchConfigFeature = useCallback(async () => {
    const response = await axios({
      url: process.env.REACT_APP_FEATURE_CONF,
      method: 'get'
    })
    if (response.data) {
      setLocalStorageConfigFeature(response.data)
    }
  }, [])

  const handleLoadInstitution = useCallback(() => {
    const sessionInstitution = getSession('b-bfb') as InstitutionDetail | null
    if (sessionInstitution?.id !== currentInstitution?.id) {
      setCurrentInstitution(sessionInstitution)
    }
  }, [currentInstitution])

  const handleInitialize = useCallback(() => {
    setIsInitialized(false)
    Promise.all([
      handleFetchUsersProfle(),
      handleFetchInstitutions(),
      handleFetchConfigFeature()
    ]).finally(() => {
      setIsInitialized(true)
    })
    handleLoadInstitution()
  }, [
    handleFetchUsersProfle,
    handleFetchInstitutions,
    handleFetchConfigFeature,
    handleLoadInstitution
  ])

  useLayoutEffect(() => {
    const currentPath = getAppPathname()

    const requiresAuth = URL_WITH_AUTH.some((protectedPath) =>
      currentPath.startsWith(protectedPath)
    )

    if (!requiresAuth) {
      return
    }

    handleInitialize()
  }, [handleInitialize, currentInstitution?.id])

  const contextValue = {
    profile,
    isGold,
    hasMultipleInstitutions,
    institutions,
    handleInitialize,
    isInitialized,
    isSidebarOpen,
    setIsSidebarOpen,
    currentInstitution,
    setCurrentInstitution,
    isAccountApproved,
    handleLoadInstitution
  }

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  )
}

export const useAppContext = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppContext must be used within an AppContextProvider')
  }
  return context
}

export default AppContextProvider
